import type { SetupFileChip, SetupFileLoaded, SetupFileStatus } from "~/helpers/aiChat/setupFiles";
import type { StatusMessage } from "~/helpers/onboardingUploadStatus";
import type { UseFernieSetupFilesReturn } from "~/hooks/useFernieSetupFiles";

import { Box, Button, CircularProgress, Icon, IconButton } from "@mui/material";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { RetryButton } from "~/components/Buttons";
import { CaptionText } from "~/components/Typographies";
import { stringFormatDate } from "~/helpers/timestampFormat";

interface FernieSetupFilesProps {
	setupFiles: UseFernieSetupFilesReturn;
}

const STATUS_LABEL_KEY: Record<SetupFileStatus, string> = {
	failed: "fernieChat.setupFiles.notUploaded",
	uploaded: "fernieChat.setupFiles.uploaded",
	uploading: "fernieChat.setupFiles.uploading"
};

const LIST_SX = { display: "flex", flexDirection: "column", gap: 0.5, listStyle: "none", m: 0, p: 0 } as const;

const FernieSetupFiles = ({ setupFiles }: FernieSetupFilesProps) => {
	const { t } = useTranslation("common");
	const outsideTitleId = useId();
	const { chips, dismissAttachment, loadedUploads, outsideUploads, prepareOutsideUpload, retryAttachment } = setupFiles;

	if (chips.length === 0 && outsideUploads.length === 0 && loadedUploads.length === 0) return null;

	const describe = (message: StatusMessage) =>
		"translationKey" in message ? t(message.translationKey, message.params) : message.text;

	const renderLoaded = (loaded: SetupFileLoaded | undefined) =>
		loaded && (
			<CaptionText sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
				{loaded.areaLabelKeys.length > 0
					? t("fernieChat.setupFiles.loaded", {
							areas: loaded.areaLabelKeys.map((labelKey) => t(labelKey)).join(", "),
							date: stringFormatDate(loaded.importedAt)
						})
					: t("fernieChat.setupFiles.loadedNoAreas", { date: stringFormatDate(loaded.importedAt) })}
			</CaptionText>
		);

	const renderChip = ({ failure, fileName, key, loaded, retryable, status }: SetupFileChip) => (
		<Box
			component="li"
			key={key}
			sx={{
				backgroundColor: "action.hover",
				borderRadius: "6px",
				display: "flex",
				flexDirection: "column",
				px: 1,
				py: 0.5
			}}
		>
			<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
				<Icon sx={{ color: "text.secondary", fontSize: 14 }}>description</Icon>
				<CaptionText noWrap sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 500, minWidth: 0 }}>
					{fileName}
				</CaptionText>
				{status === "uploading" && <CircularProgress size={12} />}
				<CaptionText sx={{ color: "text.secondary", fontSize: "0.7rem" }}>{t(STATUS_LABEL_KEY[status])}</CaptionText>
				{status === "failed" && retryable && (
					<RetryButton
						onClick={() => retryAttachment(key)}
						size="small"
						startIcon={undefined}
						sx={{ fontSize: "0.7rem", minWidth: 0, py: 0 }}
						variant="text"
					/>
				)}
				{status === "failed" && (
					<IconButton
						aria-label={t("fernieChat.setupFiles.dismiss", { fileName })}
						onClick={() => dismissAttachment(key)}
						size="small"
						sx={{ height: 16, width: 16 }}
					>
						<Icon sx={{ fontSize: 12 }}>close</Icon>
					</IconButton>
				)}
			</Box>
			{renderLoaded(loaded)}
			{failure && <CaptionText sx={{ color: "error.main", fontSize: "0.7rem" }}>{describe(failure)}</CaptionText>}
		</Box>
	);

	return (
		<Box
			sx={{
				borderColor: "divider",
				borderTop: "1px solid",
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				maxHeight: 200,
				overflowY: "auto",
				px: 2,
				py: 0.75
			}}
		>
			{(chips.length > 0 || loadedUploads.length > 0) && (
				<Box aria-label={t("fernieChat.setupFiles.listLabel")} component="ul" sx={LIST_SX}>
					{chips.map(renderChip)}
					{loadedUploads.map(({ fileName, key, loaded }) => (
						<Box component="li" key={key} sx={{ display: "flex", flexDirection: "column", px: 1, py: 0.5 }}>
							<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
								<Icon sx={{ color: "text.secondary", fontSize: 14 }}>description</Icon>
								<CaptionText noWrap sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 500, minWidth: 0 }}>
									{fileName}
								</CaptionText>
							</Box>
							{renderLoaded(loaded)}
						</Box>
					))}
				</Box>
			)}
			{outsideUploads.length > 0 && (
				<>
					<CaptionText id={outsideTitleId} sx={{ color: "text.secondary" }}>
						{t("fernieChat.setupFiles.outsideTitle")}
					</CaptionText>
					<Box aria-labelledby={outsideTitleId} component="ul" sx={LIST_SX}>
						{outsideUploads.map((upload) => (
							<Box
								component="li"
								key={`${upload.sourceId}:${upload.sourceVersion}`}
								sx={{ display: "flex", flexDirection: "column" }}
							>
								<Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
									<Icon sx={{ color: "text.secondary", fontSize: 14 }}>description</Icon>
									<CaptionText noWrap sx={{ flex: 1, fontSize: "0.75rem", minWidth: 0 }}>
										{upload.fileName}
									</CaptionText>
									<Button
										aria-label={t("fernieChat.setupFiles.prepareFile", { fileName: upload.fileName })}
										onClick={() => prepareOutsideUpload(upload)}
										size="small"
										sx={{ fontSize: "0.7rem", minWidth: 0, py: 0 }}
									>
										{t("fernieChat.setupFiles.prepare")}
									</Button>
								</Box>
								{renderLoaded(upload.loaded)}
							</Box>
						))}
					</Box>
				</>
			)}
		</Box>
	);
};

export default FernieSetupFiles;
